import { Spin } from "antd";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Accordion } from "react-bootstrap";
import { pifGet, pifPost } from "../../../helpers/api_helper";
import {
  GET_PIF_SyncDetail,
  POST_PIF_RetryJob,
  POST_PIF_ResyncJob,
} from "../../../helpers/url_helper";
import {
  PIF_QUEUE_STATUS,
  PIF_JOB_TYPE,
  PIF_FAILOUT_REASON,
  pifDateTime,
  pifDateOnly,
} from "./pifConstants";
import {
  successMessage,
  errorMessage,
  customConfirmDialog,
} from "../CommonFunctions";

const fmt = pifDateTime;

const mono = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-all",
  fontFamily: "monospace",
  fontSize: 12,
  background: "var(--bs-body-bg, #1e1e2d)",
  border: "1px solid var(--bs-border-color, #333)",
  borderRadius: 4,
  padding: 8,
  maxHeight: 320,
  overflow: "auto",
};

const SyncDetail = () => {
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const companyIdEnc = AuthUser?.CompanyIDEncrypt;
  const { PrimaryIdParam } = useParams();

  let queueId = 0;
  try {
    queueId = Number(window.atob(PrimaryIdParam));
  } catch {
    queueId = Number(PrimaryIdParam) || 0;
  }

  const [isLoading, setIsLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [steps, setSteps] = useState([]);

  const getData = async () => {
    setIsLoading(true);
    try {
      const res = await pifGet(
        `${GET_PIF_SyncDetail}?companyIdEnc=${encodeURIComponent(
          companyIdEnc
        )}&queueId=${queueId}`
      );
      setJob(res?.Job || null);
      setHistory(res?.History || []);
      setSteps(res?.Steps || []);
    } catch {
      errorMessage("Could not load the sync detail.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = () => {
    customConfirmDialog(
      "Retry job",
      "Re-queue this job for another sync attempt?",
      async () => {
        setIsLoading(true);
        try {
          const res = await pifPost(POST_PIF_RetryJob, {
            CompanyIdEnc: companyIdEnc,
            QueueId: queueId,
          });
          res?.IsSuccess
            ? successMessage(res?.Message || "Re-queued.")
            : errorMessage(res?.Message || "Could not retry.");
        } catch {
          errorMessage("Could not retry the job.");
        }
        getData();
      },
      true
    );
  };

  const resync = () => {
    customConfirmDialog(
      "Re-sync to Tally",
      "Push the invoice's current data to Tally? The existing voucher will be updated.",
      async () => {
        setIsLoading(true);
        try {
          const res = await pifPost(POST_PIF_ResyncJob, {
            CompanyIdEnc: companyIdEnc,
            QueueId: queueId,
          });
          res?.IsSuccess
            ? successMessage(res?.Message || "Re-queued for Tally.")
            : errorMessage(res?.Message || "Could not re-sync.");
        } catch {
          errorMessage("Could not re-sync the job.");
        }
        getData();
      },
      true
    );
  };

  const s = job ? PIF_QUEUE_STATUS[job.Status] || { label: job.Status, badge: "secondary" } : null;
  const canRetry = job && (job.Status === 4 || job.Status === 5);
  // Done — re-push after an invoice edit. Not for a cancellation job (JobType 4): re-running it
  // just re-cancels; there is nothing to "re-sync".
  const canResync = job && job.Status === 3 && job.JobType !== 4;

  // group steps by attempt
  const attempts = [...new Set(steps.map((x) => x.AttemptNo))].sort((a, b) => b - a);

  return (
    <div className="m-1">
      <Spin size="large" spinning={isLoading} tip={"Loading..."} style={{ position: "fixed" }}>
        <div className="card mb-2">
          <div className="card-body p-2 d-flex justify-content-between align-items-center">
            <div>
              <Link to="/sync-queue" className="btn btn-outline-primary btn-sm me-2">
                <i className="fa fa-arrow-left me-1" /> Back
              </Link>
              <span className="h5">
                {job?.InvoiceNo || (job ? `Job #${job.QueueId}` : "Sync detail")}
              </span>
            </div>
            <div>
              <button className="btn btn-outline-primary btn-sm me-2" onClick={getData}>
                <i className="fa fa-refresh me-1" /> Refresh
              </button>
              {canRetry && (
                <button className="btn btn-warning btn-sm" onClick={retry}>
                  <i className="fa fa-redo me-1" /> Retry
                </button>
              )}
              {canResync && (
                <button className="btn btn-outline-warning btn-sm" onClick={resync}>
                  <i className="fa fa-redo me-1" /> Re-sync to Tally
                </button>
              )}
            </div>
          </div>
        </div>

        {job && (
          <div className="card mb-2">
            <div className="card-body p-3">
              <div className="row">
                <div className="col-md-3">
                  <small className="text-muted d-block">Status</small>
                  <span className={`badge badge-${s.badge} light`}>{s.label}</span>
                </div>
                <div className="col-md-3">
                  <small className="text-muted d-block">Job type</small>
                  {PIF_JOB_TYPE[job.JobType] || job.JobType}
                </div>
                <div className="col-md-3">
                  <small className="text-muted d-block">Attempts</small>
                  {job.AttemptCount}/{job.MaxAttempts}
                </div>
                <div className="col-md-3">
                  <small className="text-muted d-block">Invoice date</small>
                  {pifDateOnly(job.InvoiceDate)}
                </div>
              </div>
              {job.LastError && (
                <div className="alert alert-danger py-2 mt-2 mb-0">
                  <strong>{PIF_FAILOUT_REASON[job.LastFailoutReason] || "Error"}:</strong>{" "}
                  {job.LastError}
                </div>
              )}
              <div className="mt-2">
                <small className="text-muted">Idempotency key: </small>
                <code>{job.IdempotencyKey}</code>
              </div>
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div className="card mb-2">
            <div className="card-body p-3">
              <h6 className="mb-2">Outcome</h6>
              {history.map((h) => {
                const hs = PIF_QUEUE_STATUS[h.FinalStatus] || { label: h.FinalStatus, badge: "secondary" };
                return (
                  <div key={h.HistoryId} className="d-flex flex-wrap gap-3">
                    <span>
                      <span className={`badge badge-${hs.badge} light`}>{hs.label}</span>
                    </span>
                    <span>
                      <span className="text-muted">Attempts:</span> {h.Attempts}
                    </span>
                    <span>
                      <span className="text-muted">Tally voucher:</span>{" "}
                      {h.TallyVoucherNumber || "—"}
                    </span>
                    <span>
                      <span className="text-muted">Finished:</span> {fmt(h.CreatedOn)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body p-2">
            <h6 className="mb-2 px-1">Attempt history</h6>
            {attempts.length === 0 ? (
              <p className="px-1 text-muted">No steps have run yet.</p>
            ) : (
              <Accordion defaultActiveKey={String(attempts[0])}>
                {attempts.map((a) => {
                  const rows = steps
                    .filter((x) => x.AttemptNo === a)
                    .sort((x, y) => x.StepOrder - y.StepOrder);
                  const failed = rows.some(
                    (r) => r.HttpStatus === 0 || r.HttpStatus >= 400 || (r.Outcome && r.Outcome !== 1)
                  );
                  return (
                    <Accordion.Item eventKey={String(a)} key={a}>
                      <Accordion.Header>
                        Attempt {a} — {rows.length} step(s){" "}
                        <span className={`badge ms-2 badge-${failed ? "danger" : "success"} light`}>
                          {failed ? "had errors" : "ok"}
                        </span>
                      </Accordion.Header>
                      <Accordion.Body>
                        {rows.map((r) => (
                          <div key={r.LogId} className="mb-2">
                            <div className="d-flex justify-content-between">
                              <strong>
                                #{r.StepOrder} — HTTP {r.HttpStatus} ({r.DurationMs} ms)
                              </strong>
                              <small className="text-muted">{fmt(r.CreatedOn)}</small>
                            </div>
                            {r.RequestXml ? (
                              <details>
                                <summary className="text-muted small">Request XML</summary>
                                <div style={mono}>{r.RequestXml}</div>
                              </details>
                            ) : null}
                            <div style={mono}>{r.ResponseBody || "(empty response)"}</div>
                          </div>
                        ))}
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            )}
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default SyncDetail;
